import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import { toDollars } from "common/utils/formatting";
import Icon from "common/components/Icon";
import { useAuthentication } from "common/auth/hooks";
import { Feature } from "common/types";
import { CartPriceSummary } from "common/types/priceSummary";
import { useBuildNext, useNavigate } from "common/router/hooks";
import errorImages from "common/constants/errorImages";

import PriceSummary from "client/common/components/display/PriceSummary";
import EmployeePricingNote from "client/common/components/content/EmployeePricingNote";
import { urls } from "client/routes";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useFeatures } from "client/clinics/hooks";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { FreeShippingProgressSurface } from "client/analytics/types";

import { useActiveCartQuery, useIsCartItemUpdating, useIsCartUpdating } from "../hooks";
import CartItem from "./CartItem";
import CrossSells from "./CrossSells";
import PromoCodes from "./PromoCodes";
import CartMessages from "./shared/CartMessages";
import FreeShippingProgress from "./shared/FreeShippingProgress";
import { useCartContext } from "../context/CartContext";
import { CartDrawerProps } from "../stackables";

import styles from "../styles/CartDrawer.module.less";

const Cart = ({ addedCartItemId }: CartDrawerProps) => {
  const buildNext = useBuildNext();
  const { isAuthenticated } = useAuthentication();
  const { data: cart, isLoading: isCartLoading, error: cartError } = useActiveCartQuery();
  const { isCartPrebuilt, isCartMutationSuccess } = useCartContext();
  const isCartItemUpdating = useIsCartItemUpdating();
  const isCartUpdating = useIsCartUpdating();
  const { close } = useCurrentDrawer();
  const navigate = useNavigate();

  const checkout = () => {
    close();
    if (isAuthenticated) {
      navigate(urls.checkout.start());
    } else {
      navigate(
        urls.accounts.lookup({
          search: { next: buildNext(urls.checkout.start()) },
        }),
      );
    }
  };

  if (isCartLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!cart || cartError) {
    return <DrawerLayout.FetchError error={cartError} entityName="cart" />;
  }

  const isEmptyCart = isCartPrebuilt
    ? !cart.items.length && isCartMutationSuccess
    : !cart.items.length;

  return (
    <>
      <DrawerLayout.Content>
        {/* Always render messages, whether cart is empty or not */}
        <CartMessages cart={cart} />
        {isEmptyCart && !isCartUpdating ? (
          <DrawerLayout.Error
            title="Your cart is empty!"
            description="Browse our online store or view your active prescriptions to add items to your cart"
            image={errorImages.EmptyCart}
            imageAlt="Nothing found"
          />
        ) : (
          <>
            <div className={styles.items}>
              {cart.items.map((cartItem) => (
                <CartItem
                  key={cartItem.id}
                  cart={cart}
                  cartItem={cartItem}
                  isJustAdded={cartItem.id === addedCartItemId}
                />
              ))}
            </div>
            <div>
              <CrossSells cartId={cart.id} />
            </div>
            <div>
              <PromoCodes cart={cart} />
            </div>
            <FreeShippingProgress
              cart={cart}
              surface={FreeShippingProgressSurface.CartDrawer}
              hasShopLink={true}
              onNavigate={close}
            />
            <div className={styles.summary}>
              <PriceSummary.ExternalTax<CartPriceSummary>
                data={{
                  ...cart,
                  shipping: cart.is_delivery_info_complete ? cart.shipping : null,
                  is_shipping_final: cart.is_delivery_info_complete,
                  total_label: "Estimated Total",
                }}
                tax={null}
                isTaxFinal={false}
                styles={{
                  rows: styles.rows,
                  row: styles.row,
                  subrows: styles.subrows,
                  total: styles.total,
                }}
                onNavigate={close}
              />
              {cart.should_show_employee_pricing_note && (
                <EmployeePricingNote savings={cart.employee_savings} />
              )}
            </div>
          </>
        )}
      </DrawerLayout.Content>
      {!isEmptyCart && (
        <DrawerLayout.Actions isSticky={true}>
          <DrawerLayout.Actions.Button
            isLoading={isCartItemUpdating || isCartUpdating}
            onClick={checkout}
            disabled={!!cart.warning_messages.length}
          >
            Proceed to checkout · {toDollars(cart.total)}
          </DrawerLayout.Actions.Button>
        </DrawerLayout.Actions>
      )}
    </>
  );
};

const CartDrawer = ({ addedCartItemId: rawAddedCartItemId }: CartDrawerProps) => {
  const { close } = useCurrentDrawer();
  const features = useFeatures();
  const addedCartItemId = features[Feature.ProductCardAddToCart] ? rawAddedCartItemId : null;

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title={
          addedCartItemId ? (
            <span className={styles.added}>
              <Icon icon={faCheck} className={styles.addedIcon} />
              Added to cart
            </span>
          ) : (
            "Shopping cart"
          )
        }
        onClose={close}
      />
      <Cart addedCartItemId={addedCartItemId} />
    </DrawerLayout>
  );
};

export default CartDrawer;
